import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/api";

import { AuthShell } from "../shell/AuthShell";
import { useAuth } from "./AuthContext";

// Phase 2a customer-accounts FR-6 — landing page for the
// /login/magic?token=... link in the email. Auto-consumes the token on
// mount and redirects to /portal, or surfaces "expired / used" if the
// server rejects.
export function MagicLinkConsumePage(): JSX.Element {
  const [params] = useSearchParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const consumed = useRef(false);
  const [status, setStatus] = useState<"working" | "error">("working");

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const token = params.get("token") ?? "";
    if (!token) {
      setStatus("error");
      return;
    }
    auth
      .consumeMagicLink(token)
      .then(() => navigate("/portal", { replace: true }))
      .catch((err) => {
        if (err instanceof ApiError) {
          setStatus("error");
        } else {
          setStatus("error");
        }
      });
  }, [auth, navigate, params]);

  if (status === "working") {
    return (
      <AuthShell eyebrow="Welcome back" title="Signing you in…" description="Hold on a moment.">
        <div className="flex justify-center py-4" aria-live="polite">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-red border-t-transparent" />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Sign-in link"
      title="This link expired"
      description="Head back to the sign-in screen to request a fresh link, or use your password."
      footer={
        <Link to="/sign-in" className="font-semibold text-brand-white underline-offset-2 hover:underline">
          ← Back to sign in
        </Link>
      }
    >
      <Alert variant="destructive" role="alert">
        This sign-in link has expired or already been used.
      </Alert>
    </AuthShell>
  );
}
